import type { FieldError } from '@Infra/FetchLib/ApiError';

export type GrantKind = 'role' | 'user';

export type GrantDraft = {
	readonly kind: GrantKind;
	readonly id: number;
	/** The sent value of the access level. */
	readonly level: string;
};

type ServerMessages = Readonly<Record<string, string>>;

const USABLE_BY_PATH = 'usableBy';

/**
 * Immutable state of the « usable by » lines of a dossier type: role and user lines with a level.
 * Server errors are held by path (`usableBy`, `usableBy.<index>.*`) and cleared when the lines are edited.
 */
class SubjectTypeGrantsFormData {
	public readonly usableBy: readonly GrantDraft[];
	public readonly serverMessages: ServerMessages;

	constructor(usableBy: readonly GrantDraft[], serverMessages: ServerMessages = {}) {
		this.usableBy = Object.freeze([...usableBy]);
		this.serverMessages = Object.freeze({ ...serverMessages });
		Object.freeze(this);
	}

	static createEmpty(): SubjectTypeGrantsFormData {
		return new SubjectTypeGrantsFormData([]);
	}

	static fromGrants(grants: readonly GrantDraft[]): SubjectTypeGrantsFormData {
		return new SubjectTypeGrantsFormData(
			grants.map((grant) => ({ kind: grant.kind, id: grant.id, level: grant.level })),
		);
	}

	/** A role or user already granted leaves the instance unchanged: one line per beneficiary. */
	addGrant(kind: GrantKind, id: number, level: string): SubjectTypeGrantsFormData {
		if (this.hasGrant(kind, id)) {
			return this;
		}

		return this.withGrants([...this.usableBy, { kind, id, level }]);
	}

	setGrantLevel(index: number, level: string): SubjectTypeGrantsFormData {
		return this.withGrants(
			this.usableBy.map((grant, position) => (position === index ? { ...grant, level } : grant)),
		);
	}

	removeGrant(index: number): SubjectTypeGrantsFormData {
		return this.withGrants(this.usableBy.filter((_grant, position) => position !== index));
	}

	hasGrant(kind: GrantKind, id: number): boolean {
		return this.usableBy.some((grant) => grant.kind === kind && grant.id === id);
	}

	/** Keeps the messages of the `usableBy` paths, the first message of a path winning. */
	applyServerErrors(fieldErrors: readonly FieldError[]): SubjectTypeGrantsFormData {
		const serverMessages: Record<string, string> = {};

		for (const { field, message } of fieldErrors) {
			if (field === USABLE_BY_PATH || field.startsWith(`${USABLE_BY_PATH}.`)) {
				serverMessages[field] ??= message;
			}
		}

		return new SubjectTypeGrantsFormData(this.usableBy, serverMessages);
	}

	/** Server paths `usableBy.<index>.*`: the line at `index` targets nobody valid or exceeds the caller's level. */
	getGrantServerMessage(index: number): string | null {
		const prefix = `${USABLE_BY_PATH}.${index}`;
		const path = Object.keys(this.serverMessages).find(
			(candidate) => candidate === prefix || candidate.startsWith(`${prefix}.`),
		);

		return this.messageAt(`${prefix}.id`) ?? (path === undefined ? null : this.messageAt(path));
	}

	/** Server path `usableBy`: a message about the lines as a whole. */
	getUsableByServerMessage(): string | null {
		return this.messageAt(USABLE_BY_PATH);
	}

	private messageAt(path: string): string | null {
		return this.serverMessages[path] ?? null;
	}

	private withGrants(usableBy: readonly GrantDraft[]): SubjectTypeGrantsFormData {
		return new SubjectTypeGrantsFormData(usableBy);
	}
}

export default SubjectTypeGrantsFormData;
